import type { SignalPayload } from '@shared/protocol'
import { initialCongestion, readHealth, stepCongestion, type CongestionState } from '@/lib/congestion'
import { degradationFor } from '@/lib/screenCapture'
import {
    encodingFor, loosestLimit, sanitizeLimit, smoothPlayout, type ReceiveLimit
} from '@/lib/receiveQuality'
import { localRelayCapacity, pairKey, planRelays, sanitizeIds } from '@/lib/relayPlan'
import { sampleConnection, type TrackSample } from '@/lib/streamQuality'
import { DEFAULT_ENCODER, applySendCodec, preferCodec, type ShareEncoder } from '@/lib/videoEncoder'

export type PeerConnectionState = 'new' | 'connecting' | 'connected' | 'failed' | 'closed'

export interface PeerMeshOptions {
    /** Id do proprio participante — usado pra decidir quem cede em colisao. */
    selfId: string
    iceServers: RTCIceServer[]
    /** Encaminha o payload pro peer via servidor de sinalizacao. */
    sendSignal: (to: string, data: SignalPayload) => void
    /**
     * Stream do peer chegou. O `streamId` e o que diz QUAL transmissao e: o
     * mesmo id que o publicador anuncia nas publicacoes dele. Sem ele, tela e
     * camera da mesma pessoa chegariam indistinguiveis.
     */
    onStream: (peerId: string, streamId: string, stream: MediaStream) => void
    /** Todas as streams daquele peer sumiram (saiu da sala ou caiu). */
    onPeerGone: (peerId: string) => void
    /**
     * Como esta a conexao COM aquele peer. E o unico sinal que a UI tem de que
     * a midia nao vai chegar: sem ele, NAT simetrico sem TURN vira tile preto
     * permanente e sem explicacao.
     *
     * `connecting` cobre tanto o handshake inicial quanto as tentativas de
     * recuperacao (ainda ha esperanca); `failed` so sai quando a malha DESISTE
     * daquele peer, depois de esgotar os ICE restarts.
     */
    onPeerState?: (peerId: string, state: PeerConnectionState) => void
    /** Injetavel pra teste — por padrao usa o RTCPeerConnection do browser. */
    createConnection?: (config: RTCConfiguration) => RTCPeerConnection
}

/**
 * Teto de ICE restarts por peer. Cada restart dispara `negotiationneeded` e,
 * portanto, sinalizacao: sem teto, um peer que nunca conecta gera trafego
 * indefinido no socket e pode atravessar o limite de 60 mensagens por segundo,
 * derrubando a sinalizacao ate dos peers que estavam saudaveis.
 */
const MAX_ICE_RESTARTS = 5

/**
 * Piso do bitrate por conexao, em bits/s.
 *
 * O orcamento de upload e dividido pelo numero de espectadores, e numa sala
 * cheia essa divisao chega a numeros em que o video nao e mais video. Abaixo
 * disso e melhor estourar um pouco o orcamento do que mandar uma imagem que
 * ninguem consegue usar — e nesse ponto quem devia trocar de caminho e a sala
 * (modo SFU), nao o encoder.
 */
const MIN_PEER_BITRATE = 600_000
/** Primeira tentativa e imediata; a partir da segunda, 2s, 4s, 8s, 16s. */
const ICE_RESTART_BASE_DELAY = 2_000
const ICE_RESTART_MAX_DELAY = 30_000
/** Sem `connected` nesse prazo, tenta de novo (vale pro handshake e pos-restart). */
const CONNECT_TIMEOUT = 10_000
/** `disconnected` costuma voltar sozinho; so age depois desse tempo. */
const DISCONNECT_GRACE = 5_000
/** Ceiling for a stream this peer forwards as a relay; re-encoding can't add detail the source didn't send. */
const RELAY_BITRATE = 3_000_000
/** A child that doesn't get the relayed video by then goes back to direct. */
const RELAY_CONFIRM_TIMEOUT = 12_000
/** Room changes come in bursts (join, publish, connect); plan once they settle. */
const REPLAN_DELAY = 1_000
const HEALTH_INTERVAL = 2_000

interface PeerEntry {
    /** Id do peer. O `syncTracks` precisa dele pra rearmar e reportar estado. */
    id: string
    pc: RTCPeerConnection
    /** O lado "educado" cede na colisao de offers (perfect negotiation). */
    polite: boolean
    makingOffer: boolean
    ignoreOffer: boolean
    senders: RTCRtpSender[]
    /**
     * Timer que dispara o ICE restart quando a conexao fica em `disconnected`
     * por mais de 5 s sem voltar sozinha. Sem ele o video congela sem acao.
     */
    disconnectTimer?: ReturnType<typeof setTimeout>
    /**
     * Timeout de conexao inicial: se a conexao nao chegar em `connected` em
     * 10 s, tenta reiniciar o ICE. Cancelado quando chega em `connected`.
     */
    connectTimer?: ReturnType<typeof setTimeout>
    /** Restart agendado pelo backoff. Existe pra nao empilhar duas tentativas. */
    restartTimer?: ReturnType<typeof setTimeout>
    /** Quantos ICE restarts ja foram gastos. Zerado quando a conexao sobe. */
    iceRestarts: number
    /** Esgotou as tentativas: para de reiniciar e reporta `failed` pra UI. */
    gaveUp: boolean
    /**
     * Trilhas que o outro lado anunciou na ultima descricao aplicada (as
     * linhas `a=msid:` do SDP). E o que separa a offer que traz midia nova da
     * offer que so reinicia o ICE.
     */
    remoteMedia: Set<string>
    /** Caps this peer asked for, by the id of my stream they apply to. */
    receiveLimits: Map<string, ReceiveLimit>
    congestion: CongestionState
    viaTurn: boolean
    /** What this peer said about relaying for others. */
    capacity?: { relayOk: boolean; cores: number }
    /** Publisher side: waits for this child to confirm the relayed video. */
    relayTimer?: ReturnType<typeof setTimeout>
}

/** Child side: streams of one publisher that arrive through a relay. */
interface InboundRoute {
    via: string
    streamIds: Set<string>
    /** Stream ids already confirmed to the publisher. */
    confirmed: string
}

/** Relay side: what one publisher asked me to forward. */
interface ForwardPlan {
    streamIds: Set<string>
    children: Set<string>
}

/**
 * Malha P2P: uma RTCPeerConnection por par de participantes, com
 * "perfect negotiation" (padrao do WebRTC pra colisao de offers).
 *
 * Cada pessoa publica ate duas transmissoes (tela e camera), entao a conexao
 * do par carrega de zero a varias trilhas e renegocia sozinha quando alguem
 * comeca ou para. O servidor so encaminha os payloads — o video nunca passa
 * por ele.
 */
export class PeerMesh {
    private readonly options: PeerMeshOptions
    private readonly peers = new Map<string, PeerEntry>()
    private localStreams: MediaStream[] = []
    /**
     * De qual stream cada sender nasceu.
     *
     * `RTCRtpSender` nao expoe isso, e e o dado que decide se da pra trocar a
     * trilha por dentro (mesma stream, msid intacto) ou se precisa renegociar.
     * `WeakMap` porque a chave e o proprio sender: quando a conexao morre, a
     * entrada some junto sem ninguem precisar limpar.
     */
    private readonly streamOfSender = new WeakMap<RTCRtpSender, string>()
    /** Orcamento TOTAL de upload de video, em bits/s. `0` = sem teto. */
    private uploadBudget = 0
    /** Fatia que cabe a cada conexao agora — derivada do orcamento. */
    private peerBitrate = 0
    /** Quem codifica o video que sai daqui: placa de video ou processador. */
    private encoder: ShareEncoder = DEFAULT_ENCODER

    /** Streams received from each peer, by stream id; a relay forwards from here. */
    private readonly remoteStreams = new Map<string, Map<string, MediaStream>>()
    /** Publisher side: child → relay currently planned. */
    private readonly relayRoutes = new Map<string, string>()
    /** Publisher side: stream ids last announced to each routed child. */
    private readonly routeIds = new Map<string, string>()
    /** Publisher side: children that stored their route, so the relay may start. */
    private readonly relayAcked = new Set<string>()
    /** Publisher side: my streams each child already gets through its relay. */
    private readonly relayConfirmed = new Map<string, Set<string>>()
    private readonly badRelayPairs = new Set<string>()
    /** Publisher side: last plan sent to each relay, to skip repeats. */
    private readonly sentPlans = new Map<string, string>()
    /** Relay side: what each publisher asked me to forward. */
    private readonly forwardPlans = new Map<string, ForwardPlan>()
    /** Child side: publisher → route through a relay. */
    private readonly inboundRoutes = new Map<string, InboundRoute>()
    /** Caps my own tiles asked for, by `origin::streamId`. */
    private readonly ownLimits = new Map<string, ReceiveLimit | null>()
    private readonly applying = new WeakMap<RTCRtpSender, { again: boolean }>()
    private readonly receiverOfTrack = new WeakMap<MediaStreamTrack, RTCRtpReceiver>()
    private planTimer?: ReturnType<typeof setTimeout>
    private healthTimer?: ReturnType<typeof setInterval>
    private checkingHealth = false
    private closed = false

    constructor(options: PeerMeshOptions) {
        this.options = options
    }

    /** Ajusta a malha pro conjunto atual de participantes (sem contar voce). */
    syncPeers(peerIds: string[]): void {
        const wanted = new Set(peerIds)
        for (const id of [...this.peers.keys()]) {
            if (!wanted.has(id)) this.removePeer(id)
        }
        for (const id of wanted) {
            if (!this.peers.has(id)) this.createPeer(id)
        }
        // Entrou ou saiu gente: a fatia de cada um mudou.
        this.rebalance()
        this.schedulePlan()
    }

    /** Define TODAS as suas transmissoes de uma vez (lista vazia = nenhuma). */
    setLocalStreams(streams: MediaStream[]): void {
        const wasSharing = this.videoStreamIds().length > 0
        this.localStreams = streams
        this.peerBitrate = this.directShare()
        for (const entry of this.peers.values()) this.syncTracks(entry)
        if (wasSharing !== this.videoStreamIds().length > 0) this.announceCapacity()
        this.schedulePlan()
    }

    /**
     * Orcamento TOTAL de upload de video, em bits/s. `0` deixa o navegador
     * decidir.
     *
     * Total, e nao por conexao. Na malha a mesma imagem e codificada e enviada
     * uma vez PARA CADA espectador: um teto de 6 Mbps aplicado em cada uma das
     * cinco conexoes de uma sala de seis pede 30 Mbps de upload. Quem tem essa
     * banda e raro, e o que acontecia com o resto era o oposto do pretendido —
     * a fila do link enche, o WebRTC perde pacote e a imagem trava para TODO
     * mundo, inclusive pros peers que estavam saudaveis. Dividir o orcamento e
     * o que faz o teto proteger em vez de so existir.
     */
    setMaxBitrate(bitrate: number): void {
        this.uploadBudget = bitrate
        // Forced: the source size may have changed too, and the receive caps scale from it.
        this.rebalance(true)
    }

    /**
     * Troca o encoder com a transmissao no ar.
     *
     * Vale na hora pelo codec de envio, que troca sem renegociar; a ordem do
     * SDP tambem e atualizada pra que a proxima negociacao ja nasca certa.
     */
    setEncoder(encoder: ShareEncoder): void {
        if (encoder === this.encoder) return
        this.encoder = encoder
        for (const entry of this.peers.values()) {
            for (const sender of entry.senders) {
                const transceiver = transceiverOf(entry.pc, sender)
                if (transceiver && sender.track?.kind === 'video') preferCodec(transceiver, encoder)
                void this.applyParameters(entry, sender)
            }
        }
    }

    /** Asks `peerId` for a lighter copy of one of their streams (`null` lifts it). */
    requestReceiveLimit(peerId: string, streamId: string, limit: ReceiveLimit | null): void {
        this.ownLimits.set(limitKey(peerId, streamId), limit)
        this.sendUpstreamLimit(peerId, streamId)
    }

    async handleSignal(from: string, data: SignalPayload): Promise<void> {
        if (data.kind === 'receive-limit') {
            this.applyReceiveLimit(from, data.streamId, sanitizeLimit(data.limit))
            return
        }
        if (data.kind.startsWith('relay-')) {
            this.handleRelaySignal(from, data)
            return
        }
        if (data.kind !== 'description' && data.kind !== 'candidate') return
        const entry = this.peers.get(from) ?? this.createPeer(from)
        const { pc } = entry

        try {
            if (data.kind === 'description') {
                const description = data.description
                const collision = description.type === 'offer'
                    && (entry.makingOffer || pc.signalingState !== 'stable')

                entry.ignoreOffer = !entry.polite && collision
                if (entry.ignoreOffer) return

                // O lado educado aceita a offer mesmo em colisao: o
                // setRemoteDescription faz o rollback implicito.
                await pc.setRemoteDescription(description)

                const media = announcedMedia(description.sdp)
                const novaMidia = [...media].some((line) => !entry.remoteMedia.has(line))
                entry.remoteMedia = media

                if (description.type === 'offer') {
                    await pc.setLocalDescription()
                    if (pc.localDescription) {
                        this.send(from, { kind: 'description', description: pc.localDescription.toJSON() })
                    }
                }
                /**
                 * Negociacao fechada: so agora existe a lista de codecs que os
                 * dois lados aceitaram, e so dentro dela da pra escolher o
                 * codec de envio. Antes disso `applyParameters` nao tem do que
                 * escolher e o navegador manda com o primeiro da answer.
                 */
                if (pc.signalingState === 'stable') {
                    for (const sender of entry.senders) void this.applyParameters(entry, sender)
                }
                /**
                 * O outro lado abriu uma negociacao NOVA.
                 *
                 * Isso vale tanto quanto uma trilha adicionada por aqui, e
                 * precisa devolver o orcamento de ICE restarts pelo mesmo
                 * motivo: quem tinha desistido deste peer ficava desistido pra
                 * sempre. Se A desiste de B e depois B comeca a transmitir,
                 * quem renegocia e B — A so armava o vigia e, quando ele
                 * disparava, o `scheduleIceRestart` saia calado por ja estar no
                 * teto. Resultado: tile preto que so voltava saindo e entrando
                 * na sala.
                 *
                 * So em `offer`: a `answer` chega no meio de uma tentativa
                 * NOSSA, e zerar o contador ali daria restarts sem fim.
                 *
                 * E so em offer que TRAZ midia nova. O ICE restart do outro
                 * lado tambem chega como offer, e contar ele aqui fazia um
                 * lado devolver o orcamento do outro a cada tentativa: com o
                 * ICE sem fechar, os dois se revezavam em restarts pra sempre
                 * (um a cada ~10 s, sem backoff) e nenhum chegava a desistir —
                 * o tile ficava em "conectando" em vez de dizer que nao vai.
                 */
                if (description.type === 'offer' && novaMidia) this.renewRecovery(entry)
                else this.armConnectWatchdog(from, entry)
                return
            }

            if (data.candidate) {
                await pc.addIceCandidate(data.candidate).catch((err) => {
                    if (!entry.ignoreOffer) console.warn('[mesh] falha ao adicionar candidato ICE', err)
                })
            }
        } catch (error) {
            // Candidato perdido depois de um rollback e esperado; o resto vira log.
            if (!entry.ignoreOffer) console.warn('[mesh] falha ao tratar sinal', error)
        }
    }

    removePeer(peerId: string): void {
        const entry = this.peers.get(peerId)
        if (!entry) return
        // Timer vazado aqui vira ICE restart numa conexao ja fechada.
        this.clearRecoveryTimers(entry)
        if (entry.relayTimer) clearTimeout(entry.relayTimer)
        entry.pc.onnegotiationneeded = null
        entry.pc.onicecandidate = null
        entry.pc.ontrack = null
        entry.pc.onconnectionstatechange = null
        entry.pc.close()
        this.peers.delete(peerId)
        this.forgetRelays(peerId)
        this.options.onPeerGone(peerId)
        this.options.onPeerState?.(peerId, 'closed')
    }

    close(): void {
        this.closed = true
        if (this.planTimer) clearTimeout(this.planTimer)
        if (this.healthTimer) clearInterval(this.healthTimer)
        this.planTimer = undefined
        this.healthTimer = undefined
        for (const id of [...this.peers.keys()]) this.removePeer(id)
    }

    /**
     * Medidas de video de todas as conexoes, por id de trilha.
     *
     * O que EU envio aparece somado entre os pares: na malha a mesma imagem
     * sai uma vez para cada espectador, e o total e o que responde "quanto de
     * upload isto esta me custando".
     */
    async collectStats(): Promise<Map<string, TrackSample>> {
        const samples = new Map<string, TrackSample>()
        // Um instante so pra rodada inteira: misturar relogios de conexoes
        // diferentes bagunçaria a diferenca entre duas leituras.
        const now = Date.now()
        await Promise.all(
            [...this.peers.values()].map((entry) => sampleConnection(entry.pc, samples, now))
        )
        return samples
    }

    /** Somente leitura — usado pelos testes e pela barra de diagnostico. */
    get peerIds(): string[] {
        return [...this.peers.keys()]
    }

    // ── interno ───────────────────────────────────────────────────────────────

    /**
     * Recalcula a fatia de cada conexao e reaplica quando ela muda.
     *
     * Sai calado quando o numero nao mudou: `syncPeers` roda a cada mexida na
     * sala, e `setParameters` numa conexao em negociacao e um round-trip que
     * nao precisa acontecer a toa.
     */
    private rebalance(force = false): void {
        const share = this.directShare()
        if (share === this.peerBitrate && !force) return
        this.peerBitrate = share
        for (const entry of this.peers.values()) {
            for (const sender of entry.senders) void this.applyParameters(entry, sender)
        }
    }

    /** Budget slice per connection that still gets my video straight from me. */
    private directShare(): number {
        if (this.uploadBudget <= 0) return 0
        const ids = this.videoStreamIds()
        const direct = ids.length === 0
            ? this.peers.size
            : [...this.peers.keys()].filter((id) => ids.some((streamId) => !this.relayConfirmed.get(id)?.has(streamId))).length
        return Math.max(MIN_PEER_BITRATE, Math.floor(this.uploadBudget / Math.max(1, direct)))
    }

    private applyReceiveLimit(peerId: string, streamId: unknown, limit: ReceiveLimit | null) {
        const entry = this.peers.get(peerId)
        if (!entry || typeof streamId !== 'string') return
        if (limit) entry.receiveLimits.set(streamId, limit)
        else entry.receiveLimits.delete(streamId)
        for (const sender of entry.senders) {
            if (this.streamOfSender.get(sender) === streamId) void this.applyParameters(entry, sender)
        }
        // A forwarded stream: what I pull from upstream must cover this child too.
        for (const [origin, plan] of this.forwardPlans) {
            if (plan.streamIds.has(streamId)) this.sendUpstreamLimit(origin, streamId)
        }
    }

    private videoStreamIds(): string[] {
        return this.localStreams.filter(hasVideo).map((stream) => stream.id)
    }

    /** Asks whoever sends me `origin`'s stream for the loosest cap among my tiles and my children. */
    private sendUpstreamLimit(origin: string, streamId: string) {
        const key = limitKey(origin, streamId)
        // No request from my own tile means full quality, same as a direct viewer that never asked.
        const limits: (ReceiveLimit | null)[] = [this.ownLimits.get(key) ?? null]
        const plan = this.forwardPlans.get(origin)
        if (plan?.streamIds.has(streamId)) {
            for (const child of plan.children) limits.push(this.peers.get(child)?.receiveLimits.get(streamId) ?? null)
        }
        const route = this.inboundRoutes.get(origin)
        const upstream = route?.streamIds.has(streamId) ? route.via : origin
        this.send(upstream, { kind: 'receive-limit', streamId, limit: loosestLimit(limits) })
    }

    private announceCapacity(only?: PeerEntry) {
        const capacity = localRelayCapacity(this.videoStreamIds().length > 0)
        for (const entry of only ? [only] : this.peers.values()) {
            if (entry.pc.connectionState === 'connected') this.send(entry.id, { kind: 'relay-capacity', ...capacity })
        }
    }

    private handleRelaySignal(from: string, data: SignalPayload) {
        const entry = this.peers.get(from)
        if (!entry) return
        switch (data.kind) {
            case 'relay-capacity': {
                const cores = Number(data.cores)
                entry.capacity = {
                    relayOk: data.relayOk === true,
                    cores: Number.isFinite(cores) ? Math.min(256, Math.max(0, Math.round(cores))) : 0
                }
                this.schedulePlan()
                return
            }
            case 'relay-plan':
                this.applyForwardPlan(from, sanitizeIds(data.streamIds), sanitizeIds(data.children))
                return
            case 'relay-route':
                this.applyInboundRoute(from, typeof data.via === 'string' ? data.via : null, sanitizeIds(data.streamIds))
                return
            case 'relay-ack':
                if (data.via && this.relayRoutes.get(from) === data.via) {
                    this.relayAcked.add(from)
                    this.sendPlans()
                }
                return
            case 'relay-ok': {
                if (!this.relayAcked.has(from)) return
                const mine = new Set(this.videoStreamIds())
                const confirmed = this.relayConfirmed.get(from) ?? new Set<string>()
                for (const id of sanitizeIds(data.streamIds)) if (mine.has(id)) confirmed.add(id)
                if (confirmed.size === 0) return
                this.relayConfirmed.set(from, confirmed)
                if (entry.relayTimer) clearTimeout(entry.relayTimer)
                entry.relayTimer = undefined
                this.syncTracks(entry)
                this.rebalance()
                return
            }
            case 'relay-lost':
                this.relayFailed(from)
                return
        }
    }

    // ── relay: publisher side ──────────────────────────────────────────────────

    private schedulePlan() {
        if (this.planTimer || this.closed) return
        this.planTimer = setTimeout(() => {
            this.planTimer = undefined
            this.replan()
        }, REPLAN_DELAY)
    }

    private replan() {
        const streamIds = this.videoStreamIds()
        const viewers = [...this.peers.values()].map((entry) => ({
            id: entry.id,
            relayOk: !!entry.capacity?.relayOk,
            cores: entry.capacity?.cores ?? 0,
            connected: entry.pc.connectionState === 'connected',
            viaTurn: entry.viaTurn
        }))
        const next = streamIds.length === 0
            ? new Map<string, string>()
            : planRelays(viewers, this.relayRoutes, this.badRelayPairs)
        const idsKey = streamIds.join(',')

        for (const [child, relay] of [...this.relayRoutes]) {
            if (next.get(child) !== relay) this.dropRoute(child)
        }
        for (const [child, relay] of next) {
            if (this.relayRoutes.get(child) === relay && this.routeIds.get(child) === idsKey) continue
            const isNew = this.relayRoutes.get(child) !== relay
            this.relayRoutes.set(child, relay)
            this.routeIds.set(child, idsKey)
            this.send(child, { kind: 'relay-route', via: relay, streamIds })
            if (isNew) this.armRelayTimer(child)
        }
        this.sendPlans()
        this.rebalance()
    }

    /** Back to direct: restore my senders first, then tell the child. */
    private dropRoute(child: string) {
        if (!this.relayRoutes.has(child)) return
        this.relayRoutes.delete(child)
        this.routeIds.delete(child)
        this.relayAcked.delete(child)
        const hadConfirmed = this.relayConfirmed.delete(child)
        const entry = this.peers.get(child)
        if (!entry) return
        if (entry.relayTimer) clearTimeout(entry.relayTimer)
        entry.relayTimer = undefined
        if (hadConfirmed) this.syncTracks(entry)
        this.send(child, { kind: 'relay-route', via: null, streamIds: [] })
    }

    private relayFailed(child: string) {
        const relay = this.relayRoutes.get(child)
        if (!relay) return
        this.badRelayPairs.add(pairKey(relay, child))
        this.dropRoute(child)
        this.sendPlans()
        this.rebalance()
        this.schedulePlan()
    }

    private armRelayTimer(child: string) {
        const entry = this.peers.get(child)
        if (!entry) return
        if (entry.relayTimer) clearTimeout(entry.relayTimer)
        entry.relayTimer = setTimeout(() => {
            entry.relayTimer = undefined
            if (!this.relayConfirmed.has(child)) this.relayFailed(child)
        }, RELAY_CONFIRM_TIMEOUT)
    }

    private sendPlans() {
        const streamIds = this.videoStreamIds()
        const byRelay = new Map<string, string[]>()
        for (const [child, relay] of this.relayRoutes) {
            if (!this.relayAcked.has(child)) continue
            byRelay.set(relay, [...(byRelay.get(relay) ?? []), child])
        }
        for (const relay of new Set([...byRelay.keys(), ...this.sentPlans.keys()])) {
            const children = (byRelay.get(relay) ?? []).sort()
            const key = `${streamIds.join(',')}|${children.join(',')}`
            if (this.sentPlans.get(relay) === key) continue
            if (children.length > 0) this.sentPlans.set(relay, key)
            else this.sentPlans.delete(relay)
            if (this.peers.has(relay)) {
                this.send(relay, { kind: 'relay-plan', streamIds: children.length > 0 ? streamIds : [], children })
            }
        }
    }

    // ── relay: relay and child side ────────────────────────────────────────────

    private applyForwardPlan(origin: string, streamIds: string[], children: string[]) {
        const before = this.forwardPlans.get(origin)
        const valid = children.filter((id) => id !== origin && id !== this.options.selfId && this.peers.has(id))
        if (valid.length > 0 && streamIds.length > 0) {
            this.forwardPlans.set(origin, { streamIds: new Set(streamIds), children: new Set(valid) })
        } else {
            this.forwardPlans.delete(origin)
        }
        for (const child of new Set([...(before?.children ?? []), ...valid])) {
            const entry = this.peers.get(child)
            if (entry) this.syncTracks(entry)
        }
        for (const streamId of new Set([...(before?.streamIds ?? []), ...streamIds])) {
            this.sendUpstreamLimit(origin, streamId)
        }
    }

    private resyncForwards(origin: string) {
        const plan = this.forwardPlans.get(origin)
        if (!plan) return
        for (const child of plan.children) {
            const entry = this.peers.get(child)
            if (entry) this.syncTracks(entry)
        }
    }

    private applyInboundRoute(origin: string, via: string | null, streamIds: string[]) {
        const previous = this.inboundRoutes.get(origin)
        const usable = via && via !== origin && this.peers.has(via) ? via : null
        if (usable) this.inboundRoutes.set(origin, { via: usable, streamIds: new Set(streamIds), confirmed: previous?.via === usable ? previous.confirmed : '' })
        else this.inboundRoutes.delete(origin)
        this.send(origin, { kind: 'relay-ack', via: usable })

        if (usable) {
            for (const stream of this.remoteStreams.get(usable)?.values() ?? []) {
                if (streamIds.includes(stream.id)) this.emitStream(usable, stream)
            }
        }
        if (previous?.via !== (usable ?? undefined)) {
            const prefix = `${origin}::`
            for (const key of this.ownLimits.keys()) {
                if (key.startsWith(prefix)) this.sendUpstreamLimit(origin, key.slice(prefix.length))
            }
        }
    }

    /** The publisher a stream really belongs to when it arrives through a relay. */
    private originOf(from: string, streamId: string): string | undefined {
        for (const [origin, route] of this.inboundRoutes) {
            if (route.via === from && route.streamIds.has(streamId)) return origin
        }
        return undefined
    }

    private emitStream(from: string, stream: MediaStream) {
        const origin = this.originOf(from, stream.id)
        this.options.onStream(origin ?? from, stream.id, stream)
        if (origin) this.confirmRelay(origin)
    }

    private confirmRelay(origin: string) {
        const route = this.inboundRoutes.get(origin)
        if (!route) return
        const streams = this.remoteStreams.get(route.via)
        const arrived = [...route.streamIds].filter((id) => {
            const stream = streams?.get(id)
            return !!stream && hasVideo(stream)
        }).sort()
        const key = arrived.join(',')
        if (arrived.length === 0 || route.confirmed === key) return
        route.confirmed = key
        this.send(origin, { kind: 'relay-ok', streamIds: arrived })
    }

    private storeRemote(peerId: string, stream: MediaStream) {
        let streams = this.remoteStreams.get(peerId)
        if (!streams) {
            streams = new Map()
            this.remoteStreams.set(peerId, streams)
        }
        if (streams.get(stream.id) === stream) return
        streams.set(stream.id, stream)
        // The publisher dropping a track from the stream has to reach my children too.
        if (typeof stream.addEventListener === 'function') {
            stream.addEventListener('removetrack', () => this.resyncForwards(peerId))
        }
    }

    /** Peer left: whatever routes, plans and relays went through it. */
    private forgetRelays(peerId: string) {
        this.remoteStreams.delete(peerId)
        this.sentPlans.delete(peerId)
        this.relayRoutes.delete(peerId)
        this.routeIds.delete(peerId)
        this.relayAcked.delete(peerId)
        this.relayConfirmed.delete(peerId)
        for (const [child, relay] of [...this.relayRoutes]) {
            if (relay === peerId) this.dropRoute(child)
        }

        const plan = this.forwardPlans.get(peerId)
        this.forwardPlans.delete(peerId)
        for (const child of plan?.children ?? []) {
            const entry = this.peers.get(child)
            if (entry) this.syncTracks(entry)
        }

        this.inboundRoutes.delete(peerId)
        this.routesLost(peerId)
        const prefix = `${peerId}::`
        for (const key of [...this.ownLimits.keys()]) if (key.startsWith(prefix)) this.ownLimits.delete(key)
        this.sendPlans()
        this.rebalance()
        this.schedulePlan()
    }

    /** My relay through `via` is gone: ask each publisher for the direct copy back. */
    private routesLost(via: string) {
        for (const [origin, route] of [...this.inboundRoutes]) {
            if (route.via !== via) continue
            this.inboundRoutes.delete(origin)
            this.send(origin, { kind: 'relay-lost' })
        }
    }

    private ensureHealthLoop() {
        if (this.healthTimer || this.closed) return
        this.healthTimer = setInterval(() => void this.checkHealth(), HEALTH_INTERVAL)
    }

    /** Congestion brake and TURN detection, per connection that carries my video. */
    private async checkHealth() {
        if (this.checkingHealth) return
        this.checkingHealth = true
        try {
            await Promise.all([...this.peers.values()].map(async (entry) => {
                const { pc } = entry
                if (pc.connectionState !== 'connected' || typeof pc.getStats !== 'function') return
                if (!entry.senders.some((sender) => sender.track?.kind === 'video')) return
                let report: RTCStatsReport
                try {
                    report = await pc.getStats()
                } catch {
                    return
                }
                const health = readHealth(report)
                if (health.viaTurn !== entry.viaTurn) {
                    entry.viaTurn = health.viaTurn
                    this.schedulePlan()
                }
                const next = stepCongestion(entry.congestion, health)
                const changed = next.ratio !== entry.congestion.ratio
                entry.congestion = next
                for (const sender of entry.senders) {
                    const streamId = this.streamOfSender.get(sender)
                    // A forwarded stream's size follows what upstream sends, so its scale is redone each round.
                    const forwardedCap = !!streamId && entry.receiveLimits.has(streamId)
                        && !this.localStreams.some((stream) => stream.id === streamId)
                    if (changed || forwardedCap) void this.applyParameters(entry, sender)
                }
            }))
        } finally {
            this.checkingHealth = false
        }
    }

    private send(to: string, data: SignalPayload) {
        this.options.sendSignal(to, data)
    }

    private createPeer(peerId: string): PeerEntry {
        const factory = this.options.createConnection ?? ((config) => new RTCPeerConnection(config))
        const pc = factory({ iceServers: this.options.iceServers, bundlePolicy: 'max-bundle' })

        const entry: PeerEntry = {
            id: peerId,
            pc,
            // Criterio estavel e simetrico: os dois lados chegam na mesma resposta.
            polite: this.options.selfId > peerId,
            makingOffer: false,
            ignoreOffer: false,
            senders: [],
            iceRestarts: 0,
            gaveUp: false,
            remoteMedia: new Set(),
            receiveLimits: new Map(),
            congestion: initialCongestion(),
            viaTurn: false
        }
        this.peers.set(peerId, entry)

        pc.onnegotiationneeded = async () => {
            try {
                entry.makingOffer = true
                await pc.setLocalDescription()
                if (pc.localDescription) {
                    this.send(peerId, { kind: 'description', description: pc.localDescription.toJSON() })
                }
                // Agora existe handshake pra vigiar. Antes daqui nao existia:
                // vigiar conexao sem negociacao so gasta o orcamento de restarts.
                this.armConnectWatchdog(peerId, entry)
            } catch (error) {
                console.warn('[mesh] falha ao negociar', error)
            } finally {
                entry.makingOffer = false
            }
        }

        pc.onicecandidate = (event) => {
            this.send(peerId, { kind: 'candidate', candidate: event.candidate ? event.candidate.toJSON() : null })
        }

        pc.ontrack = (event) => {
            // O `streams[0]` vem do msid negociado no SDP, entao o id aqui e o
            // MESMO que o publicador anunciou — e por ele que se sabe se este
            // video e a tela ou a camera daquela pessoa.
            const stream = event.streams?.[0] ?? new MediaStream([event.track])
            if (typeof stream.getTracks === 'function' && typeof stream.addTrack === 'function') {
                if (!stream.getTracks().includes(event.track)) {
                    stream.addTrack(event.track)
                }
            }
            if (event.track?.kind === 'video') smoothPlayout(event.receiver)
            if (event.track && event.receiver) this.receiverOfTrack.set(event.track, event.receiver)
            this.storeRemote(peerId, stream)
            this.emitStream(peerId, stream)
            this.resyncForwards(peerId)
        }

        pc.onconnectionstatechange = () => {
            const state = pc.connectionState

            // Disconnected: a rede pode estar oscilando. Espera 5 s antes de
            // forcar o ICE restart — muitas vezes a conexao volta sozinha.
            if (state === 'disconnected') {
                if (!entry.disconnectTimer) {
                    entry.disconnectTimer = setTimeout(() => {
                        entry.disconnectTimer = undefined
                        if (pc.connectionState === 'disconnected') {
                            this.scheduleIceRestart(peerId, entry, 'preso em disconnected')
                        }
                    }, DISCONNECT_GRACE)
                }
            } else {
                if (entry.disconnectTimer) {
                    clearTimeout(entry.disconnectTimer)
                    entry.disconnectTimer = undefined
                }
            }

            if (state === 'failed') {
                // Relayed video stops right now; the publisher's direct copy is the quickest way back.
                this.routesLost(peerId)
                this.scheduleIceRestart(peerId, entry, 'conexao falhou')
            }

            // Conexao estabilizou: cancela os timers e devolve o orcamento de
            // restarts. Timer vazado aqui vira restart em loop.
            if (state === 'connected') {
                this.clearRecoveryTimers(entry)
                entry.iceRestarts = 0
                entry.gaveUp = false
                this.announceCapacity(entry)
            }
            this.schedulePlan()

            this.reportState(peerId, entry, state)
        }

        // Diagnostico: loga qual servidor STUN/TURN falhou e por que. Sem isso,
        // quando o firewall bloqueia ou o STUN do Google cai, nao ha como saber.
        if (typeof pc.addEventListener === 'function') {
            pc.addEventListener('icecandidateerror', (event: Event) => {
                const ice = event as RTCPeerConnectionIceErrorEvent
                console.warn('[mesh] candidato ICE falhou', {
                    url: ice.url,
                    errorCode: ice.errorCode,
                    errorText: ice.errorText,
                    peer: peerId
                })
            })
        }

        // As trilhas primeiro: e o `addTrack` que da o que negociar. So depois
        // o vigia, que se recusa a armar numa conexao que ainda nao negocia
        // nada (sala onde ninguem transmite) — armar ali queimaria o orcamento
        // inteiro de ICE restarts sem nunca ter tido o que reiniciar.
        this.syncTracks(entry)
        this.armConnectWatchdog(peerId, entry)
        return entry
    }

    /**
     * Vigia a conexao: sem `connected` no prazo, pede o proximo ICE restart.
     *
     * Rearmado depois de cada restart pro caso em que a conexao nunca chega a
     * emitir `failed` de novo (o navegador pode ficar em `connecting` por
     * bastante tempo). Como todo pedido passa pelo mesmo contador, o numero
     * total de tentativas continua limitado.
     */
    private armConnectWatchdog(peerId: string, entry: PeerEntry) {
        if (entry.connectTimer) clearTimeout(entry.connectTimer)
        // Sem negociacao nao ha o que vigiar: quem arma e a primeira offer
        // (local ou remota) ou a primeira trilha adicionada.
        if (!this.hasNegotiation(entry)) return
        entry.connectTimer = setTimeout(() => {
            entry.connectTimer = undefined
            const current = entry.pc.connectionState
            if (current === 'connected' || current === 'closed') return
            // Rechecagem no disparo: se nesse meio tempo a conexao voltou a nao
            // ter o que negociar, `restartIce()` seria no-op e a tentativa
            // sairia do orcamento a toa.
            if (!this.hasNegotiation(entry)) return
            this.scheduleIceRestart(peerId, entry, `sem conexao em ${CONNECT_TIMEOUT / 1000}s (${current})`)
        }, CONNECT_TIMEOUT)
    }

    /**
     * Essa conexao tem handshake acontecendo?
     *
     * Numa sala onde ninguem transmite — nem tela, nem camera, nem voz — a
     * conexao do par nasce vazia: `syncTracks` nao adiciona trilha,
     * `negotiationneeded` nunca dispara, `connectionState` fica em `new` e
     * `restartIce()` num pc sem localDescription e no-op puro. Contar
     * tentativas ali zera o orcamento antes da primeira negociacao de verdade.
     */
    private hasNegotiation(entry: PeerEntry): boolean {
        const { pc } = entry
        // Fora de `new` o ICE ja esta em jogo (connecting/connected/failed...).
        if (pc.connectionState !== 'new') return true
        if (pc.localDescription || pc.remoteDescription) return true
        if (entry.senders.length > 0) return true
        const transceivers = typeof pc.getTransceivers === 'function' ? pc.getTransceivers() : []
        return transceivers.length > 0
    }

    /**
     * Trilha nova nessa conexao: comeca uma negociacao de verdade agora.
     *
     * Devolve o orcamento de restarts e volta a vigiar. A desistencia NAO pode
     * ser definitiva: entre um `gaveUp` e a primeira transmissao da sala a rede
     * pode ter voltado, e sem isso o tile nasceria com a tarja vermelha e sem
     * direito a um unico ICE restart ate a pessoa sair e voltar da sala.
     */
    private renewRecovery(entry: PeerEntry) {
        const state = entry.pc.connectionState
        if (state === 'closed') return
        const desistiu = entry.gaveUp
        entry.gaveUp = false
        entry.iceRestarts = 0
        // Tira a tarja de "nao foi possivel conectar". `connecting` e o estado
        // honesto aqui: a negociacao acabou de comecar, mesmo que o pc ainda
        // esteja em `new` (quem tinha desistido nunca esta em `connected`).
        if (desistiu) this.options.onPeerState?.(entry.id, 'connecting')
        this.armConnectWatchdog(entry.id, entry)
    }

    /**
     * Pede um ICE restart respeitando teto e backoff.
     *
     * A primeira tentativa e sincrona: quando a conexao acabou de falhar, cada
     * segundo parado e video que nao chega. Da segunda em diante o intervalo
     * cresce (2s, 4s, 8s, 16s) pra nao inundar a sinalizacao, e depois de
     * `MAX_ICE_RESTARTS` a malha desiste daquele peer e avisa a UI — tile preto
     * pra sempre e pior que uma mensagem dizendo que nao vai conectar.
     */
    private scheduleIceRestart(peerId: string, entry: PeerEntry, motivo: string) {
        // Ja ha uma tentativa agendada: nao empilha outra.
        if (entry.restartTimer) return
        const state = entry.pc.connectionState
        if (state === 'closed' || state === 'connected') return

        if (entry.iceRestarts >= MAX_ICE_RESTARTS) {
            if (!entry.gaveUp) {
                entry.gaveUp = true
                this.clearRecoveryTimers(entry)
                console.warn('[mesh] desistindo do peer', { peer: peerId, restarts: MAX_ICE_RESTARTS, motivo })
                this.options.onPeerState?.(peerId, 'failed')
            }
            return
        }

        const attempt = entry.iceRestarts
        entry.iceRestarts += 1
        const delay = attempt === 0
            ? 0
            : Math.min(ICE_RESTART_BASE_DELAY * 2 ** (attempt - 1), ICE_RESTART_MAX_DELAY)
        console.warn('[mesh] ICE restart', {
            peer: peerId,
            tentativa: `${entry.iceRestarts}/${MAX_ICE_RESTARTS}`,
            motivo,
            emMs: delay
        })

        const run = () => {
            const current = entry.pc.connectionState
            if (current === 'closed' || current === 'connected') return
            entry.pc.restartIce()
            // Rearma o vigia: se este restart tambem nao subir a conexao, a
            // proxima tentativa (ou a desistencia) sai daqui.
            this.armConnectWatchdog(peerId, entry)
        }

        if (delay === 0) {
            run()
            return
        }
        entry.restartTimer = setTimeout(() => {
            entry.restartTimer = undefined
            run()
        }, delay)
    }

    private clearRecoveryTimers(entry: PeerEntry) {
        if (entry.disconnectTimer) {
            clearTimeout(entry.disconnectTimer)
            entry.disconnectTimer = undefined
        }
        if (entry.connectTimer) {
            clearTimeout(entry.connectTimer)
            entry.connectTimer = undefined
        }
        if (entry.restartTimer) {
            clearTimeout(entry.restartTimer)
            entry.restartTimer = undefined
        }
    }

    /**
     * Traduz o estado do navegador pro vocabulario da UI.
     *
     * `failed`/`disconnected` com tentativas restantes viram `connecting`: a
     * malha ainda esta agindo, e dizer "nao conectou" ali seria mentira. So
     * depois de desistir e que a pessoa ve `failed`.
     */
    private reportState(peerId: string, entry: PeerEntry, state: RTCPeerConnectionState) {
        const report = (state === 'failed' || state === 'disconnected') && !entry.gaveUp
            ? 'connecting'
            : toPeerState(state)
        this.options.onPeerState?.(peerId, report)
    }

    /**
     * Alinha as trilhas enviadas nessa conexao com o que voce publica agora.
     *
     * Conhece a diferenca entre audio e video: nunca tenta colocar video em sender de audio.
     * Reutiliza senders compativeis do mesmo kind usando replaceTrack e adiciona/remove
     * conforme necessario disparando renegociacao limpa.
     */
    private syncTracks(entry: PeerEntry) {
        const { pc } = entry
        const wantedTracks: { track: MediaStreamTrack; stream: MediaStream }[] = []
        const pushLive = (stream: MediaStream) => {
            if (typeof stream.getTracks !== 'function') return
            for (const track of stream.getTracks()) {
                if (!track.readyState || track.readyState === 'live') {
                    wantedTracks.push({ track, stream })
                }
            }
        }
        const relayed = this.relayConfirmed.get(entry.id)
        for (const stream of this.localStreams) {
            // This child already gets it from a relay.
            if (relayed?.has(stream.id) && hasVideo(stream)) continue
            pushLive(stream)
        }
        for (const [origin, plan] of this.forwardPlans) {
            if (origin === entry.id || !plan.children.has(entry.id)) continue
            const streams = this.remoteStreams.get(origin)
            for (const streamId of plan.streamIds) {
                const stream = streams?.get(streamId)
                if (stream) pushLive(stream)
            }
        }

        const currentSenders = typeof pc.getSenders === 'function' ? pc.getSenders() : entry.senders
        const matchedSenders = new Set<RTCRtpSender>()
        const unhandledWanted: { track: MediaStreamTrack; stream: MediaStream }[] = []

        // Passo 1: Manter senders que já transmitem a trilha exata
        for (const wanted of wantedTracks) {
            const exactSender = currentSenders.find(
                (s) => !matchedSenders.has(s) && s.track === wanted.track
            )
            if (exactSender) {
                matchedSenders.add(exactSender)
                void this.applyParameters(entry, exactSender)
            } else {
                unhandledWanted.push(wanted)
            }
        }

        /**
         * Passo 2: trocar a trilha por dentro, mas SO dentro da mesma stream.
         *
         * `replaceTrack` nao renegocia — e essa e a graca dele: troca a camera
         * sem novo handshake. Mas por nao renegociar, ele tambem NAO muda o
         * msid ja anunciado no SDP: o vinculo trilha↔stream do outro lado
         * continua apontando pra stream antiga.
         *
         * Quem publica anuncia `streamId: stream.id` da stream NOVA, enquanto
         * quem recebe segue enxergando a ANTIGA. Os dois nunca casam, e o tile
         * do espectador fica preto — mesmo com o video chegando. Acontecia ao
         * trocar de tela, ligar/desligar fundo da camera e no republish depois
         * de reconectar.
         *
         * Trocar de stream, entao, precisa passar por remove + add (passos 3 e
         * 4), que renegociam e levam o msid certo.
         */
        const stillNeeded: { track: MediaStreamTrack; stream: MediaStream }[] = []
        for (const wanted of unhandledWanted) {
            const compatibleSender = currentSenders.find((s) => (
                !matchedSenders.has(s)
                && s.track
                && s.track.kind === wanted.track.kind
                // A trilha atual deste sender pertence a MESMA stream que a nova?
                && this.streamOfSender.get(s) === wanted.stream.id
            ))
            if (compatibleSender) {
                matchedSenders.add(compatibleSender)
                if (typeof compatibleSender.replaceTrack === 'function') {
                    void compatibleSender.replaceTrack(wanted.track).catch(() => {
                        // Ignora
                    })
                }
                void this.applyParameters(entry, compatibleSender)
            } else {
                stillNeeded.push(wanted)
            }
        }

        // Passo 3: Adicionar trilhas restantes que precisam de novos senders (addTrack dispara onnegotiationneeded)
        let adicionou = false
        for (const wanted of stillNeeded) {
            try {
                const sender = pc.addTrack(wanted.track, wanted.stream)
                // Antes da offer sair: `negotiationneeded` dispara depois desta
                // volta do loop de eventos, e a offer ja leva o codec na frente.
                const transceiver = transceiverOf(pc, sender)
                if (transceiver && wanted.track.kind === 'video') preferCodec(transceiver, this.encoder)
                matchedSenders.add(sender)
                entry.senders.push(sender)
                // Guarda de qual stream este sender nasceu: e o que permite o
                // passo 2 saber quando pode reaproveitar sem quebrar o msid.
                this.streamOfSender.set(sender, wanted.stream.id)
                adicionou = true
                void this.applyParameters(entry, sender)
            } catch (err) {
                console.warn('[mesh] falha ao adicionar trilha ao peer', err)
            }
        }

        // Passo 4: Remover senders que não são mais necessários
        for (const sender of currentSenders) {
            if (!matchedSenders.has(sender) && sender.track) {
                try {
                    pc.removeTrack(sender)
                    // Sender aposentado: sem isto o mapa cresceria a cada troca
                    // de tela ate a conexao fechar.
                    this.streamOfSender.delete(sender)
                } catch {
                    // Ignora
                }
            }
        }

        entry.senders = typeof pc.getSenders === 'function' ? pc.getSenders() : entry.senders.filter((s) => matchedSenders.has(s))
        if (entry.senders.some((sender) => sender.track?.kind === 'video')) this.ensureHealthLoop()

        // Trilha nova = negociacao nova: hora de vigiar de novo e, se o peer
        // tinha desistido, de dar a ele uma segunda chance.
        if (adicionou) this.renewRecovery(entry)
    }

    /**
     * Teto de bitrate, o que ceder sob aperto e o codec de envio.
     *
     * Tudo num `setParameters` so: cada chamada precisa dos parametros lidos
     * logo antes, e duas seguidas sobre a mesma leitura derrubariam uma delas.
     */
    private async applyParameters(entry: PeerEntry, sender: RTCRtpSender) {
        if (!sender.track || sender.track.kind !== 'video') return
        // Overlapping get/setParameters on one sender fail with InvalidModificationError; coalesce into one rerun.
        const state = this.applying.get(sender)
        if (state) {
            state.again = true
            return
        }
        const current = { again: false }
        this.applying.set(sender, current)
        try {
            do {
                current.again = false
                await this.applyParametersOnce(entry, sender)
            } while (current.again)
        } finally {
            this.applying.delete(sender)
        }
    }

    private async receivedSize(track: MediaStreamTrack): Promise<{ width?: number; height?: number }> {
        const receiver = this.receiverOfTrack.get(track)
        if (typeof receiver?.getStats !== 'function') return {}
        try {
            let size: { width?: number; height?: number } = {}
            const report = await receiver.getStats()
            report.forEach((stat) => {
                const item = stat as { type?: string; frameWidth?: number; frameHeight?: number }
                if (item.type === 'inbound-rtp' && item.frameWidth && item.frameHeight) {
                    size = { width: item.frameWidth, height: item.frameHeight }
                }
            })
            return size
        } catch {
            return {}
        }
    }

    private async applyParametersOnce(entry: PeerEntry, sender: RTCRtpSender) {
        if (!sender.track || sender.track.kind !== 'video') return
        try {
            const streamId = this.streamOfSender.get(sender)
            const requested = (streamId && entry.receiveLimits.get(streamId)) || null
            const settings = sender.track.getSettings?.() ?? {}
            // A forwarded track reports no size; read it from what arrives. Before getParameters, which can't cross an await.
            const source = settings.width && settings.height ? settings : requested ? await this.receivedSize(sender.track) : {}
            if (!sender.track) return
            const params = sender.getParameters()
            if (!params.encodings || params.encodings.length === 0) params.encodings = [{}]
            const limit = encodingFor(requested, source)
            const local = this.localStreams.some((stream) => stream.id === streamId)
            const bitrate = [local ? this.peerBitrate : RELAY_BITRATE, limit.maxBitrate ?? 0].filter((value) => value > 0)
            const ceiling = bitrate.length > 0 ? Math.round(Math.min(...bitrate) * entry.congestion.ratio) : 0
            for (const encoding of params.encodings) {
                if (ceiling > 0) encoding.maxBitrate = ceiling
                else delete encoding.maxBitrate
                encoding.scaleResolutionDownBy = limit.scaleResolutionDownBy
                if (limit.maxFramerate) encoding.maxFramerate = limit.maxFramerate
                else delete encoding.maxFramerate
            }
            const degradation = degradationFor(sender.track)
            if (degradation) params.degradationPreference = degradation
            applySendCodec(params, this.encoder)
            await sender.setParameters(params)
        } catch (error) {
            console.warn('[mesh] nao deu pra ajustar os parametros de envio', error)
        }
    }
}

/**
 * Trilhas anunciadas num SDP: cada linha `a=msid:` e uma trilha (stream +
 * trilha) que aquele lado manda.
 *
 * O ICE restart reaproveita as mesmas linhas, so troca as credenciais de ICE;
 * comecar a transmitir, ou trocar de tela, traz uma linha que nao existia.
 * `a=msid-semantic` fica de fora sozinho, porque nao tem o `:` logo depois.
 */
function announcedMedia(sdp: string | undefined): Set<string> {
    return new Set((sdp ?? '').split(/\r?\n/).filter((line) => line.startsWith('a=msid:')))
}

function hasVideo(stream: MediaStream): boolean {
    return typeof stream.getVideoTracks === 'function'
        ? stream.getVideoTracks().length > 0
        : typeof stream.getTracks === 'function' && stream.getTracks().some((track) => track.kind === 'video')
}

function limitKey(origin: string, streamId: string): string {
    return `${origin}::${streamId}`
}

/** O transceiver dono do sender — e nele que mora a ordem dos codecs. */
function transceiverOf(pc: RTCPeerConnection, sender: RTCRtpSender): RTCRtpTransceiver | undefined {
    if (typeof pc.getTransceivers !== 'function') return undefined
    return pc.getTransceivers().find((transceiver) => transceiver.sender === sender)
}

function toPeerState(state: RTCPeerConnectionState): PeerConnectionState {
    switch (state) {
        case 'connected': return 'connected'
        case 'connecting': return 'connecting'
        case 'failed':
        case 'disconnected': return 'failed'
        case 'closed': return 'closed'
        default: return 'new'
    }
}
